import Grid from '@mui/material/Grid'
import { useQueryClient } from '@tanstack/react-query'
import { Unit, useUnitByName } from 'api/units/units.api'
import i18n from 'i18next'
import moment from 'moment'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { Card } from 'ui/atoms'
import { registerDefaultProps, registerUiConfigComponent } from 'ui/uiConfig/factory'
import UiConfigComponent from 'ui/uiConfig/UiConfigComponent'
import Datetime from 'utils/datetime/datetime'
import { useSnapshot } from 'valtio'
import PeriodPicker from 'views/AvailabilityView/components/AvailabilityWidget/components/PeriodPicker/PeriodPicker'
import PlannedStopSettings from 'views/AvailabilityView/components/AvailabilityWidget/components/PlannedStopModal/PlannedStopSettings/PlannedStopSettings'
import DeviationSettings from '../FollowupProduction/DeviationSettings/DeviationSettings'
import styles from './Availability.module.less'
import type { AvailabilityProps, AvailabilityUnitWidget } from './Availability.types'

export const AVAILABILITY_QUERY_KEY = 'availabilityQueryKey'

const defaultProps: Omit<AvailabilityProps, `datasets` | `children` | `systemId`> = {
  availabilityUnits: [
    {
      unit_name: '',
      unit_display_name: '',
      kpi: {
        ui_config_id: NaN,
      },
      chart: {
        ui_config_id: NaN,
      },
    },
  ],
}
registerUiConfigComponent(`availability`, Availability, {
  isSingletonComponent: true,
  title: i18n.t(`Availability for Units`),
})
registerDefaultProps(`availability`, defaultProps)

type AvailabilityWidgetProps = {
  systemId: number
  unitWidget: AvailabilityUnitWidget
}
function AvailabilityWidget({ systemId, unitWidget }: AvailabilityWidgetProps): ReactElement {
  const { data: units } = useUnitByName({ name: unitWidget.unit_name })
  const [period, setPeriod] = useState({ startTime: moment().startOf(`month`), endTime: moment().endOf(`month`) })
  const uiConfigSnap = useSnapshot(uiConfigStore)
  const unitName = unitWidget.unit_display_name ?? unitWidget.unit_name
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  let myUnit: Unit

  if (units && units[0]) {
    myUnit = units[0]
  } else {
    return <></>
  }

  function handleOnChange(): void {
    queryClient.invalidateQueries({
      queryKey: [AVAILABILITY_QUERY_KEY],
    })
  }

  return (
    <div className={styles.Availability_Widget}>
      <Grid size={{ xs: 12 }}>
        <Card tourId={'AvailabilityWidget'} title={`${t('Availability')} - ${unitName}`}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
            style={{ overflow: `hidden` }}
          >
            <Grid
              container
              direction="column"
              size={{ xs: 4 }}
              spacing={4}
              alignItems="center"
              justifyContent="space-between"
            >
              <PeriodPicker setPeriod={setPeriod} />
              <UiConfigComponent
                datasetRefreshToken={AVAILABILITY_QUERY_KEY}
                systemId={systemId}
                id={unitWidget.kpi.ui_config_id}
                datasetStartTime={Datetime.toISOString(period.startTime)}
                datasetEndTime={Datetime.toISOString(period.endTime)}
                uid={uiConfigSnap.idToUiConfig[unitWidget.kpi.ui_config_id]?.uid}
              />
              <Grid>
                <DeviationSettings
                  startTime={Datetime.toISOString(period.startTime)}
                  endTime={Datetime.toISOString(period.endTime)}
                />
              </Grid>
              <PlannedStopSettings period={period} unit={myUnit?.id ?? 0} onRefetch={() => handleOnChange()} />
            </Grid>
            <Grid size={{ xs: 8 }}>
              <UiConfigComponent
                datasetRefreshToken={AVAILABILITY_QUERY_KEY}
                systemId={systemId}
                id={unitWidget.chart.ui_config_id}
                datasetStartTime={Datetime.toISOString(period.startTime)}
                datasetEndTime={Datetime.toISOString(period.endTime)}
                uid={uiConfigSnap.idToUiConfig[unitWidget.chart.ui_config_id]?.uid}
              />
            </Grid>
          </Grid>
        </Card>
      </Grid>
    </div>
  )
}

export default function Availability({ availabilityUnits, systemId }: AvailabilityProps): ReactElement {
  return (
    <>
      {availabilityUnits.map((unitWidget, index) => {
        return <AvailabilityWidget key={index} systemId={systemId} unitWidget={unitWidget} />
      })}
    </>
  )
}
